import { Component, Input, signal, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router } from '@angular/router';

import {
  ExpansionPanelComponent,
  AccordionItem,
  AccordionLink,
} from '../../webpagecreation/expansion-panel/expansion-panel.component';
import { DynamicPageCreationComponent } from '../../webpagecreation/dynamic-page-creation/dynamic-page-creation.component';
import { DynamicFileUploadComponent } from '../../webpagecreation/dynamic-file-upload/dynamic-file-upload.component';

import { AccordionPageService } from '../../../inputfileds/form_services/WebsiteManagement/accordian_page/accordion-page.service';
import { ApiservicesService } from '../../../Services/Apis/apiservices.service';
import { environment } from '../../../../environments/environment';
import { TranslateDirective } from '../../Utilites/Directives/translate.directive';
import { ToastService } from '../../../Services/toast/toast.service';
import { ConfigService } from '../../../Services/configservices/sasTokenConfig';

interface CreateAccordionPayload {
  title: string;
  description?: string;
  website_id: string;
  website_page_id: string;
  parent_accordion_id?: string;
  open_in_new_tab?: boolean;
  data?: { data_type_id: string }[];
}

@Component({
  selector: 'app-accordion-dynamic-page',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatDialogModule,
    MatExpansionModule,
    MatIconModule,
    MatMenuModule,
    MatProgressSpinnerModule,
    ExpansionPanelComponent,
    TranslateDirective,
  ],
  templateUrl: './accordion-dynamic-page.component.html',
  styleUrls: ['./accordion-dynamic-page.component.scss'],
})
export class AccordionDynamicPageComponent implements OnInit {
  @Input() websiteId!: string;
  @Input() pageId!: string;
  @Input() apiEndpoint!: string;
  @Input() websiteURL!: string;

  private dialog = inject(MatDialog);
  private accordionService = inject(AccordionPageService);
  private api = inject(ApiservicesService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private toast = inject(ToastService);
  private config = inject(ConfigService);

  accordionItems = signal<AccordionItem[]>([]);
  expandedIds = new Set<string>();
  selectedId = signal<string | null>(null);
  isLoading = signal(false);

  ngOnInit(): void {
    this.websiteId ||= this.route.snapshot.queryParams['websiteId'];
    this.pageId ||= this.route.snapshot.queryParams['pageId'];
    this.apiEndpoint ||= this.route.snapshot.queryParams['endpoint'];
    this.websiteURL ||= this.route.snapshot.queryParams['websiteURL'];

    if (this.websiteId && this.pageId) {
      this.loadItems();
    }
  }

  loadItems(silent = false): void {
    if (!silent) this.isLoading.set(true);
    this.api.getAccordionItems(this.websiteId, this.pageId).subscribe({
      next: (res: { rows: any[] }) => {
        const items = (res.rows || []).map((item) =>
          this.mapApiToAccordionItem(item),
        );
        this.accordionItems.set(items);
      },
      error: (err) => console.error('[Accordion] Load failed', err),
      complete: () => this.isLoading.set(false),
    });
  }

  private async openFileUpload(parent?: AccordionItem): Promise<void> {
    try {
      this.isLoading.set(true); // Show spinner while fetching token

      // 1. Fetch token ONLY when the user clicks upload
      const freshBlobUrl = await this.config.getStorageConnection();

      this.isLoading.set(false);

      const dialogRef = this.dialog.open(DynamicFileUploadComponent, {
        width: '520px',
        data: {
          title: parent ? 'Upload File to Accordion' : 'Upload File',
          service: this.api,
          questions$: this.accordionService.getCreateAccordionPage(),
          item: parent,
          isLink: false,
          id: parent?.id,
          accessUrl: freshBlobUrl, // Use the dynamic URL
          containerName: environment.ASSET_CONTAINER,
          folderDataPath: '/WEBSITE_MANAGEMENT/AccordionFiles',
          websiteURL: this.websiteURL,
        },
      });

      dialogRef.componentInstance.formSubmit.subscribe((formData: FormData) => {
        if (parent?.id) this.expandedIds.add(parent.id);
        this.api.updateAccordionData(formData, parent?.id).subscribe({
          next: () => this.loadItems(true),
          error: (err) => console.error('File upload failed', err),
        });
        dialogRef.close();
      });

      dialogRef.componentInstance.cancel.subscribe(() => dialogRef.close());
    } catch (error) {
      this.isLoading.set(false);
      this.toast.showToast(
        'danger',
        'Could not establish connection to storage.',
      );
    }
  }

  // --- Actions ---
  onAction(event: { action: string; item: AccordionItem }): void {
    const { action, item } = event;
    switch (action) {
      case 'addAccordion':
        this.addAccordion(item);
        break;
      case 'uploadFile':
        this.openFileUpload(item);
        break; // Trigger async upload
      case 'link':
        this.openAddLinkDialog(item);
        break;
      case 'edit':
        this.openEditDialog(item);
        break;
      case 'delete':
        this.confirmDelete(item);
        break;
    }
  }

  private mapApiToAccordionItem(api: any): AccordionItem {
    const children = (api.data ?? []).map((c: any) =>
      this.mapApiToAccordionItem(c),
    );
    const links = (api.accordion_content ?? []).map((c: any, idx: number) => ({
      id: c.accordion_details_id,
      label:
        c.type === 'FILES'
          ? c.title || `File ${idx + 1}`
          : c.content || `Link ${idx + 1}`,
      url: c.type === 'FILES' ? c.title : c.content,
      description: `Type: ${c.type}`,
      icon: c.type === 'FILES' ? 'fas fa-file' : 'fas fa-link',
    }));
    const id = String(api.id);
    return {
      id,
      title: api.title || api.name || 'Untitled Accordion',
      description: api.description || '',
      type: 'accordion',
      expanded: this.expandedIds.has(id),
      actions: true,
      actionTooltip: 'Actions',
      children: children.length ? children : undefined,
      links: links.length ? links : undefined,
      data: api,
    };
  }

  addAccordion(parent?: AccordionItem): void {
    const isNested = !!parent;
    const dialogRef = this.dialog.open(DynamicPageCreationComponent, {
      width: '640px',
      data: {
        pageTitle: isNested
          ? 'Add Nested Accordion Item'
          : 'Create New Accordion',
        questions$: this.accordionService.getCreateAccordionPage(),
        initialData: null,
        TabButton: true,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result?.title) return;
      const payload: CreateAccordionPayload = {
        title: result.title.trim(),
        description: result.description?.trim(),
        website_id: this.websiteId,
        website_page_id: this.pageId,
        open_in_new_tab: !!result.open_in_new_tab,
        data: (result.data ?? []).map((d: any) => ({
          data_type_id: String(d.id),
        })),
      };
      if (isNested && parent?.id) {
        payload.parent_accordion_id = parent.id;
        this.expandedIds.add(parent.id);
      }
      this.api.createAccordionPage(payload).subscribe({
        next: () => this.loadItems(true),
        error: () => this.loadItems(),
      });
    });
  }

  private openAddLinkDialog(item: AccordionItem): void {
    const dialogRef = this.dialog.open(DynamicFileUploadComponent, {
      width: '420px',
      data: {
        title: 'External Link',
        service: this.api,
        questions$: this.accordionService.getLinkAccordionData(),
        item,
        id: item.id,
        isLink: true,
      },
    });

    dialogRef.componentInstance.formSubmit.subscribe((result: any) => {
      if (!result)
        return this.toast.showToast('warning', 'Please fill all link fields');
      const payload = {
        accordion_id: item.id,
        type: 'LINKS',
        links_list: result.links_list,
      };
      this.api.updateAccordionData(payload, item.id).subscribe({
        next: () => {
          item.id && this.expandedIds.add(item.id);
          this.loadItems(true);
        },
      });
    });
  }

  private openEditDialog(item: AccordionItem): void {
    const dialogRef = this.dialog.open(DynamicPageCreationComponent, {
      width: '640px',
      data: {
        pageTitle: 'Edit Accordion',
        questions$: this.accordionService.getCreateAccordionPage(),
        initialData: item.data,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) return;
      const payload = {
        ...result,
        id: item.id,
        data: (result.data ?? []).map((d: any) => ({
          data_type_id: String(d.id),
        })),
        website_id: this.websiteId,
        website_page_id: this.pageId,
      };
      if (item.id) this.expandedIds.add(item.id);
      this.api.updateAccordionData(payload, item.id).subscribe({
        next: () => this.loadItems(true),
      });
    });
  }

  private confirmDelete(item: AccordionItem): void {
    if (!confirm('Delete this item?')) return;
    const isLink = item.type === 'links' || !!item.data?.accordion_details_id;
    const payload = isLink
      ? { accordion_details_id: item.data?.accordion_details_id ?? item.id }
      : { accordion_id: item.id };
    this.api
      .deleteAccordionData(payload)
      .subscribe({ next: () => this.loadItems(true) });
  }

  onItemExpanded = (item: AccordionItem) =>
    item.id && (this.expandedIds.add(item.id), this.selectedId.set(item.id));
  onItemCollapsed = (item: AccordionItem) =>
    item.id && this.expandedIds.delete(item.id);

  onFilesReordered(event: {
    item: AccordionItem;
    links: AccordionLink[];
  }): void {
    const { item, links } = event;
    const payload = {
      accordion_details: links.map((link: AccordionLink, idx: number) => ({
        accordion_details_id: link.id,
        data_display_order: idx + 1,
        details_display_order: idx + 1,
      })),
    };
    // Keep the parent accordion expanded after reload
    if (item.id) this.expandedIds.add(item.id);
    this.api.updateAccordionFilesSequence(payload, item.id).subscribe({
      next: () => {
        this.toast.showToast('success', 'Files reordered successfully');
        this.loadItems(true);
      },
      error: (err) => {
        console.error('[Accordion] Reorder files failed', err);
        this.toast.showToast(
          'danger',
          'Failed to save order. Please try again.',
        );
        this.loadItems(true);
      },
    });
  }

  goBack = () =>
    this.router.navigate(['/websites/WebcomponentList'], {
      queryParams: { websiteId: this.websiteId, pageId: this.pageId },
      queryParamsHandling: 'merge',
    });
}
